import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLanguage } from '../../context/LanguageContext';

const { 
  FiLink, FiShare2, FiGrid, FiCode, FiSmartphone, FiCheckCircle,
  FiCopy, FiCheck, FiSliders, FiExternalLink, FiGlobe, FiZap
} = FiIcons;

const UnityTab = () => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [ticketQty, setTicketQty] = useState(2);

  const features = [
    {
      icon: FiLink,
      title: t('solutionsPage.unity.features.f1Title') || "One Click, Infinite Reach",
      description: t('solutionsPage.unity.features.f1Desc') || "Instantly generate high-converting event URLs. Share unique, short links across any platform to track performance and drive direct registrations."
    },
    {
      icon: FiShare2,
      title: t('solutionsPage.unity.features.f2Title') || "Omnichannel Promotion",
      description: t('solutionsPage.unity.features.f2Desc') || "Broadcast your event to social channels and email lists simultaneously. Our integrated tools ensure your message lands exactly where your audience lives."
    },
    {
      icon: FiGrid,
      title: t('solutionsPage.unity.features.f3Title') || "Curated Event Series",
      description: t('solutionsPage.unity.features.f3Desc') || "Don't just promote one event—showcase your entire seasonal lineup. Group collections into beautiful galleries that encourage multi-event signups."
    },
    {
      icon: FiCode,
      title: t('solutionsPage.unity.features.f4Title') || "Native Website Embedding",
      description: t('solutionsPage.unity.features.f4Desc') || "Turn your existing website into a powerful sales engine. Embed ticketing and event info directly into your pages with a single line of code."
    },
    {
      icon: FiSmartphone,
      title: t('solutionsPage.unity.features.f5Title') || "Brand-First Mobile Widgets",
      description: t('solutionsPage.unity.features.f5Desc') || "Fully customizable, mobile-optimized widgets that feel like a native part of your site. Maintain total brand control while maximizing mobile conversions."
    }
  ];

  const embedSnippet = `<script src="https://embed.bookmein.events/v1/unity.js" data-event="summit-2026" async></script>`;

  const handleCopy = () => {
    navigator.clipboard?.writeText?.(embedSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-12 py-2">
      {/* Hero Header */}
      <div className="text-center max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 backdrop-blur-md border border-white/60 text-emerald-700 text-xs font-semibold uppercase tracking-wider shadow-sm inner-glow">
          <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
          <span>{t('solutionsPage.unity.badge') || "Conversion-First Event Embedding"}</span>
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
          {t('solutionsPage.unity.title') || "Turn Any Webpage Into a Ticket Box Office"}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          {t('solutionsPage.unity.subtitle') || "The visibility engine. Embed tickets, create promo links, and broadcast your event across the web."}
        </p>
      </div>

      {/* Interactive Embed & Widget Playground */}
      <div className="glass-panel rounded-[28px] p-6 md:p-8 border border-white/80 shadow-xl shadow-slate-200/40 inner-glow relative overflow-hidden space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-5 border-b border-slate-200/70">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <h3 className="text-lg font-bold text-slate-900 tracking-tight">{t('solutionsPage.unity.engineTitle') || "Unity Native Embed Engine & Short Links"}</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('solutionsPage.unity.engineSubtitle') || "Embed anywhere with 1 line of HTML code or share high-converting short links."}</p>
          </div>
          <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold font-mono">
            {t('solutionsPage.unity.zeroLatencyBadge') || "Zero-Latency Loading"}
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 items-center">
          {/* Code snippet & Embed Controls (7 Cols) */}
          <div className="lg:col-span-7 space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{t('solutionsPage.unity.snippetLabel') || "Single Line HTML Snippet"}</span>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                  <span>{copied ? (t('solutionsPage.unity.copied') || 'Copied to Clipboard!') : (t('solutionsPage.unity.copyCode') || 'Copy Code')}</span>
                </button>
              </div>

              <div className="p-4 rounded-2xl bg-slate-950 text-slate-200 font-mono text-xs border border-slate-800 shadow-inner overflow-x-auto">
                <code>{embedSnippet}</code>
              </div>
            </div>

            {/* Feature Perks */}
            <div className="grid sm:grid-cols-3 gap-3 pt-2">
              <div className="p-3.5 rounded-xl bg-white/70 border border-slate-200/80 space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{t('solutionsPage.unity.cssControl') || "Full CSS Control"}</span>
                </div>
                <p className="text-[11px] text-slate-500">{t('solutionsPage.unity.cssControlDesc') || "Adapts automatically to your site's font & palette."}</p>
              </div>
              <div className="p-3.5 rounded-xl bg-white/70 border border-slate-200/80 space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{t('solutionsPage.unity.zeroLatency') || "Zero Latency"}</span>
                </div>
                <p className="text-[11px] text-slate-500">{t('solutionsPage.unity.zeroLatencyDesc') || "Sub-10kb bundle cached on global Cloudflare CDN."}</p>
              </div>
              <div className="p-3.5 rounded-xl bg-white/70 border border-slate-200/80 space-y-1">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
                  <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{t('solutionsPage.unity.autoSync') || "Auto-Sync Data"}</span>
                </div>
                <p className="text-[11px] text-slate-500">{t('solutionsPage.unity.autoSyncDesc') || "Live ticket counts stay synced with your box office."}</p>
              </div>
            </div>
          </div>

          {/* Live Mobile Widget Preview (5 Cols) */}
          <div className="lg:col-span-5">
            <div className="max-w-xs mx-auto p-5 rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-200/60 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                    {t('solutionsPage.unity.liveWidget') || "Live Widget"}
                  </span>
                  <h4 className="font-bold text-slate-900 text-sm mt-1">Tech Summit 2026</h4>
                </div>
                <span className="text-sm font-extrabold text-slate-900">$249</span>
              </div>

              <div className="flex items-center justify-between py-2 text-xs text-slate-600 font-medium">
                <span>{t('solutionsPage.unity.passes') || "Passes"}</span>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setTicketQty(Math.max(1, ticketQty - 1))}
                    className="w-6 h-6 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center font-bold hover:bg-slate-200"
                  >
                    -
                  </button>
                  <span className="font-mono font-bold text-slate-900 px-1">{ticketQty}</span>
                  <button 
                    onClick={() => setTicketQty(ticketQty + 1)}
                    className="w-6 h-6 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center font-bold hover:bg-slate-200"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="pt-1">
                <button className="w-full py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-bold text-xs shadow-md shadow-emerald-500/20 hover:shadow-lg transition-all flex items-center justify-center gap-2">
                  <span>{t('solutionsPage.unity.registerNow') || "Register Now"} (${ticketQty * 249})</span>
                  <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                </button>
                <p className="text-center text-[10px] text-slate-400 mt-2">{t('solutionsPage.unity.poweredBy') || "Powered by BookMeIn.Events Unity"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The 5 Core Features Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((feat, idx) => (
          <motion.div
            key={idx}
            whileHover={{ y: -3 }}
            className={`glass-card rounded-2xl p-6 border border-white/80 shadow-md shadow-slate-100/60 hover:shadow-xl hover:border-emerald-200/80 transition-all flex flex-col justify-between ${
              idx === 4 ? 'md:col-span-2 lg:col-span-1' : ''
            }`}
          >
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-600/20 border border-emerald-200/60 flex items-center justify-center text-emerald-600 shadow-sm">
                <SafeIcon icon={feat.icon} className="w-6 h-6" />
              </div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">{feat.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed font-normal">{feat.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default UnityTab;
